import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Warranty } from '@wsphere/warranties/domain';
import { WarrantyWebService } from '../../../warranty/services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-void-warranty-dialog',
  templateUrl: './void-warranty.dialog.html',
  standalone: false,
})
export class VoidWarrantyDialog {
  constructor(
    private toastSvc: ToastService,
    private warrantySvc: WarrantyWebService
  ) {}

  @ViewChild(DialogComponent) private dialog?: DialogComponent;

  @Input() warranty?: Warranty.Model;
  @Output() readonly voided = new EventEmitter<Warranty.VoidWarrantyResponse>();
  /** Fired when the void attempt fails (e.g. the gateway rejects a refund) — lets the parent
   * page refresh the ledger without wrongly marking the warranty VOID the way `voided` would. */
  @Output() readonly voidFailed = new EventEmitter<void>();

  protected saving = false;
  protected reasonControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });

  open() {
    this.reasonControl.reset('');
    this.dialog?.open();
  }

  close() {
    if (this.saving) return;
    this.dialog?.close();
  }

  protected async submit() {
    this.reasonControl.markAsTouched();
    if (!this.warranty?.id || this.reasonControl.invalid) return;

    this.saving = true;
    try {
      const response = await this.warrantySvc.voidWarranty(this.warranty.id, this.reasonControl.value);
      this.voided.emit(response);

      this.toastSvc.show({
        type: 'success',
        title: 'Voided Warranty',
        text: 'Warranty has successfully been voided and all eligible payments were refunded.',
      });

      this.dialog?.close();
    } catch (err: unknown) {
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to void warranty',
        text:
          err instanceof Error
            ? err.message
            : (err as { error?: { message?: string } })?.error?.message || 'Unknown Error',
      });
      // A failed attempt (e.g. a rejected refund) can still write ledger data (a $0 disbursement
      // documenting the attempt), so the parent needs to refresh even though void() didn't succeed.
      this.voidFailed.emit();
    } finally {
      this.saving = false;
    }
  }
}
